import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { createHousekeepingLead, updateHousekeepingLead, deleteHousekeepingLead } from '@/lib/api';

const emptyLeadForm = {
  name: '', contact: '', notes: '', call_time: '',
  interview_pay: '', trial: '', additional_notes: '', is_archived: false
};

function LeadCard({ lead: l, idx, isArchived, onEdit, onArchive, onDelete }) {
  return (
    <div className={`group border rounded-xl px-4 py-3.5 ${idx % 2 === 0 ? 'bg-card' : 'bg-muted/20'} ${isArchived ? 'opacity-60' : ''} hover:shadow-sm transition-shadow`} data-testid="hk-lead-row">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{l.name}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
            {l.contact && <span className="text-xs text-muted-foreground">{l.contact}</span>}
            {l.call_time && <span className="text-xs text-muted-foreground">Call: {l.call_time}</span>}
            {l.interview_pay && <span className="text-xs text-muted-foreground">Pay: {l.interview_pay}</span>}
            {l.trial && <span className="text-xs text-muted-foreground">Trial: {l.trial}</span>}
          </div>
          {l.notes && <p className="text-xs text-muted-foreground mt-1">{l.notes}</p>}
        </div>
        <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onEdit}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onArchive}><Archive className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={onDelete}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
    </div>
  );
}

export function HousekeepingLeadsTab({ leads, showArchived, setShowArchived, onRefresh }) {
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyLeadForm });
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setEditing(null); setForm({ ...emptyLeadForm }); setDialog(true); };
  const openEdit = (l) => {
    setEditing(l);
    setForm({
      name: l.name, contact: l.contact || '', notes: l.notes || '',
      call_time: l.call_time || '', interview_pay: l.interview_pay || '',
      trial: l.trial || '', additional_notes: l.additional_notes || '',
      is_archived: l.is_archived || false
    });
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      if (editing) { await updateHousekeepingLead(editing.id, form); toast.success('Updated'); }
      else { await createHousekeepingLead(form); toast.success('Created'); }
      setDialog(false);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (l) => {
    try {
      await updateHousekeepingLead(l.id, { ...l, is_archived: !l.is_archived });
      toast.success(l.is_archived ? 'Restored' : 'Archived');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete permanently?')) return;
    try { await deleteHousekeepingLead(id); toast.success('Deleted'); onRefresh(); }
    catch { toast.error('Failed'); }
  };

  const active = leads.filter(l => !l.is_archived);
  const archived = leads.filter(l => l.is_archived);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Button size="sm" variant={showArchived ? 'secondary' : 'outline'} className="text-xs h-8" onClick={() => setShowArchived(!showArchived)}>
          <Archive className="h-3 w-3 mr-1" />{showArchived ? 'Hide' : 'Show'} Archived
        </Button>
        <Button onClick={openCreate} data-testid="hk-lead-add-btn">
          <Plus className="h-4 w-4 mr-1" />Add Lead
        </Button>
      </div>

      {active.length === 0 && !showArchived && (
        <div className="text-center py-12 text-muted-foreground text-sm border rounded-xl bg-muted/20">No housekeeping leads added</div>
      )}

      <div className="space-y-2" data-testid="hk-leads-list">
        {active.map((l, idx) => (
          <LeadCard key={l.id} lead={l} idx={idx} onEdit={() => openEdit(l)} onArchive={() => handleArchive(l)} onDelete={() => handleDelete(l.id)} />
        ))}
        {showArchived && archived.length > 0 && (
          <>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground pt-2 pb-1 px-1">Archived</p>
            {archived.map((l, idx) => (
              <LeadCard key={l.id} lead={l} idx={idx} isArchived onEdit={() => openEdit(l)} onArchive={() => handleArchive(l)} onDelete={() => handleDelete(l.id)} />
            ))}
          </>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Housekeeping Lead' : 'Add Housekeeping Lead'}</DialogTitle>
            <DialogDescription>Enter lead details for potential housekeepers.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="hk-lead-name-input" /></div>
              <div className="space-y-1"><Label>Contact</Label><Input value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Call Time</Label><Input value={form.call_time} onChange={e => setForm({ ...form, call_time: e.target.value })} placeholder="e.g. 2pm" /></div>
              <div className="space-y-1"><Label>Interview Pay</Label><Input value={form.interview_pay} onChange={e => setForm({ ...form, interview_pay: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label>Trial</Label><Input value={form.trial} onChange={e => setForm({ ...form, trial: e.target.value })} placeholder="e.g. Trial cleaning scheduled 3/15" /></div>
            <div className="space-y-1"><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
            <div className="space-y-1"><Label>Additional Notes</Label><Textarea value={form.additional_notes} onChange={e => setForm({ ...form, additional_notes: e.target.value })} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="hk-lead-save-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
